import { useState, useEffect, useMemo, useRef } from 'react'
import { clsx } from 'clsx'

export interface TimezoneSelectProps {
  value: string
  onChange: (value: string) => void
  timezones?: string[]
  grouped?: Record<string, string[]>
  placeholder?: string
  disabled?: boolean
  error?: string
  className?: string
  autoDetect?: boolean
}

function formatTz(tz: string): string {
  return tz.replace(/_/g, ' ')
}

export function TimezoneSelect({
  value,
  onChange,
  timezones = [],
  grouped,
  placeholder = 'Select timezone...',
  disabled = false,
  error,
  className,
  autoDetect = true,
}: TimezoneSelectProps) {
  const [search, setSearch] = useState('')
  const [isOpen, setIsOpen] = useState(false)
  const [highlightIndex, setHighlightIndex] = useState(-1)
  const containerRef = useRef<HTMLDivElement>(null)
  const inputRef = useRef<HTMLInputElement>(null)
  const listRef = useRef<HTMLDivElement>(null)

  // Auto-detect user's timezone on mount
  useEffect(() => {
    if (autoDetect && !value && typeof Intl !== 'undefined') {
      try {
        const detected = Intl.DateTimeFormat().resolvedOptions().timeZone
        if (detected && (timezones.includes(detected) || !timezones.length)) {
          onChange(detected)
        }
      } catch {
        // Ignore errors - not all environments support this
      }
    }
  }, [autoDetect, value, onChange, timezones])

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false)
        setSearch('')
      }
    }
    document.addEventListener('mousedown', handleClickOutside)
    return () => document.removeEventListener('mousedown', handleClickOutside)
  }, [])

  // Build flat list of options for keyboard navigation
  const flatOptions = useMemo((): { tz: string; region?: string }[] => {
    const searchLower = search.toLowerCase()
    if (grouped && Object.keys(grouped).length > 0) {
      const result: { tz: string; region?: string }[] = []
      for (const [region, tzs] of Object.entries(grouped).sort(([a], [b]) => a.localeCompare(b))) {
        const filtered = search
          ? tzs.filter((tz) => tz.toLowerCase().includes(searchLower))
          : tzs
        for (const tz of filtered.sort()) {
          result.push({ tz, region })
        }
      }
      return result
    }
    const list = search
      ? timezones.filter((tz) => tz.toLowerCase().includes(searchLower))
      : timezones
    return list.sort().map((tz) => ({ tz }))
  }, [timezones, grouped, search])

  // Reset highlight when options change
  useEffect(() => {
    setHighlightIndex(-1)
  }, [flatOptions])

  // Scroll highlighted item into view
  useEffect(() => {
    if (highlightIndex >= 0 && listRef.current) {
      const items = listRef.current.querySelectorAll('[data-tz-option]')
      items[highlightIndex]?.scrollIntoView({ block: 'nearest' })
    }
  }, [highlightIndex])

  const selectTimezone = (tz: string) => {
    onChange(tz)
    setIsOpen(false)
    setSearch('')
    setHighlightIndex(-1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        setIsOpen(true)
      }
      return
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault()
        setHighlightIndex((prev) => Math.min(prev + 1, flatOptions.length - 1))
        break
      case 'ArrowUp':
        e.preventDefault()
        setHighlightIndex((prev) => Math.max(prev - 1, 0))
        break
      case 'Enter':
        e.preventDefault()
        if (highlightIndex >= 0 && highlightIndex < flatOptions.length) {
          selectTimezone(flatOptions[highlightIndex].tz)
        }
        break
      case 'Escape':
        e.preventDefault()
        setIsOpen(false)
        setSearch('')
        break
    }
  }

  // Group consecutive options by region for rendering
  const renderDropdownOptions = () => {
    if (flatOptions.length === 0) {
      return (
        <div className="px-3 py-2 text-sm text-secondary-500">
          No timezones found
        </div>
      )
    }

    const elements: React.ReactNode[] = []
    let currentRegion: string | undefined

    flatOptions.forEach((opt, idx) => {
      if (opt.region && opt.region !== currentRegion) {
        currentRegion = opt.region
        elements.push(
          <div
            key={`region-${opt.region}`}
            className="px-3 py-1.5 text-xs font-semibold text-secondary-500 uppercase tracking-wider bg-secondary-50 sticky top-0"
          >
            {opt.region}
          </div>
        )
      }

      elements.push(
        <div
          key={opt.tz}
          data-tz-option
          className={clsx(
            'px-3 py-2 text-sm cursor-pointer',
            idx === highlightIndex && 'bg-primary-50 text-primary-700',
            opt.tz === value && idx !== highlightIndex && 'bg-primary-50/50 font-medium',
            idx !== highlightIndex && opt.tz !== value && 'hover:bg-secondary-50',
          )}
          onMouseDown={(e) => {
            e.preventDefault()
            selectTimezone(opt.tz)
          }}
          onMouseEnter={() => setHighlightIndex(idx)}
        >
          {formatTz(opt.tz)}
        </div>
      )
    })

    return elements
  }

  return (
    <div className={clsx('w-full', className)} ref={containerRef}>
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          className={clsx(
            'input w-full cursor-pointer',
            error && 'border-red-500 focus:border-red-500 focus:ring-red-500/20',
          )}
          placeholder={value ? formatTz(value) : placeholder}
          value={isOpen ? search : value ? formatTz(value) : ''}
          readOnly={!isOpen}
          disabled={disabled}
          onClick={() => {
            if (!disabled) {
              setIsOpen(true)
              setSearch('')
            }
          }}
          onChange={(e) => setSearch(e.target.value)}
          onKeyDown={handleKeyDown}
        />

        {/* Chevron / clear button */}
        <div className="absolute inset-y-0 right-0 flex items-center pr-3 gap-1">
          {value && !disabled && (
            <button
              type="button"
              className="text-secondary-400 hover:text-secondary-600"
              onMouseDown={(e) => {
                e.preventDefault()
                onChange('')
                setSearch('')
                setIsOpen(false)
              }}
              tabIndex={-1}
            >
              <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          )}
          <svg
            className={clsx('h-4 w-4 text-secondary-400 transition-transform', isOpen && 'rotate-180')}
            fill="none"
            viewBox="0 0 24 24"
            stroke="currentColor"
            strokeWidth={2}
          >
            <path strokeLinecap="round" strokeLinejoin="round" d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div
            ref={listRef}
            className="absolute z-50 mt-1 w-full max-h-60 overflow-auto rounded-lg border border-secondary-200 bg-white shadow-lg"
          >
            {renderDropdownOptions()}
          </div>
        )}
      </div>

      {value && (
        <p className="text-xs text-secondary-500 mt-1">
          Current time: {new Date().toLocaleTimeString('en-US', { timeZone: value, hour: '2-digit', minute: '2-digit' })}
        </p>
      )}

      {error && <p className="text-red-500 text-sm mt-1">{error}</p>}
    </div>
  )
}
